import React, { useState } from "react";
import "./loginSignup.css";
import {
  supabase,
  supabaseUrl,
  isUsingFallbackSupabaseConfig,
} from '../../supabase_client.jsx';

const DEFAULT_SIGNUP_MESSAGE =
  "Account created. Check your email to confirm your account, then log in.";
const ENV_SETUP_MESSAGE =
  "Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in doyouwannago/.env.local, then restart the app.";

function deriveRouteForType(type) {
  if (type === "Admin") return "admin";
  if (type === "Business") return "businessHome";
  return "user";
}

function formatAuthError(error) {
  const message = error?.message || "Authentication failed.";
  if (message.toLowerCase() === "failed to fetch") {
    const fallbackNote = isUsingFallbackSupabaseConfig
      ? ` The app is currently falling back to ${supabaseUrl}. ${ENV_SETUP_MESSAGE}`
      : ` Check that ${supabaseUrl} is reachable and that the publishable key is correct.`;
    return `Could not reach the configured Supabase auth endpoint.${fallbackNote}`;
  }
  return message;
}

const LoginSignup = ({ onViewSelect, onLogin }) => {
  const [action, setAction] = useState(() => {
    return sessionStorage.getItem("authMode") || "Sign Up";
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [accountType, setAccountType] = useState("User");
  const [authError, setAuthError] = useState("");

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  }

  async function ensureUserRecords(authUser, fallback = {}) {
    if (!authUser?.id) {
      throw new Error("Missing authenticated user.");
    }

    const metadata = authUser.user_metadata || {};
    const resolvedUsername =
      (fallback.username || metadata.username || authUser.email?.split("@")[0] || "")
        .trim();
    const resolvedType = fallback.accountType || metadata.accountType || "User";

    const { data: existingProfile, error: profileLookupError } = await supabase
      .from("users")
      .select("user_id, type, username")
      .eq("user_id", authUser.id)
      .maybeSingle();

    if (profileLookupError) {
      console.error("Profile lookup failed:", profileLookupError.message);
      throw profileLookupError;
    }

    let profileType = existingProfile?.type || resolvedType;

    if (!existingProfile) {
      const { error: insertError } = await supabase.from("users").insert({
        user_id: authUser.id,
        username: resolvedUsername,
        type: resolvedType,
      });

      if (insertError) {
        console.error("Insert into public.users failed:", insertError.message);
        throw insertError;
      }
    } else if (
      (resolvedUsername && !existingProfile.username) ||
      (resolvedType && !existingProfile.type)
    ) {
      const updates = {};
      if (resolvedUsername && !existingProfile.username) updates.username = resolvedUsername;
      if (resolvedType && !existingProfile.type) updates.type = resolvedType;

      const { error: updateError } = await supabase
        .from("users")
        .update(updates)
        .eq("user_id", authUser.id);

      if (updateError) {
        console.error("Update public.users failed:", updateError.message);
        throw updateError;
      }
    }

    if (profileType === "Business") {
      const { data: existingBusiness, error: businessLookupError } = await supabase
        .from("businesses")
        .select("user_id")
        .eq("user_id", authUser.id)
        .maybeSingle();

      if (businessLookupError) {
        console.error("Business lookup failed:", businessLookupError.message);
        throw businessLookupError;
      }

      if (!existingBusiness) {
        const { error: businessInsertError } = await supabase.from("businesses").insert({
          user_id: authUser.id,
          name: resolvedUsername || authUser.email?.split("@")[0] || "Business",
          has_setup_profile: false,
        });

        if (businessInsertError) {
          console.error("Insert into businesses failed:", businessInsertError.message);
          throw businessInsertError;
        }
      }
    }

    return { type: profileType };
  }

  async function routeAuthenticatedUser(authUser, fallback = {}) {
    const profile = await ensureUserRecords(authUser, fallback);
    onViewSelect(deriveRouteForType(profile.type));
  }

  async function signUp() {
    setAuthError("");

    if (isUsingFallbackSupabaseConfig) {
      console.warn("Signup is using fallback Supabase config:", supabaseUrl);
    }

    const cleanUsername = username.trim();
    const cleanEmail = email.trim().toLowerCase();

    if (!cleanUsername) {
      setAuthError("Enter a username to create your account.");
      return;
    }

    if (!cleanEmail) {
      setAuthError("Enter your email address.");
      return;
    }

    if (!password.trim()) {
      setAuthError("Enter a password to create your account.");
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email: cleanEmail,
      password,
      options: {
        data: {
          username: cleanUsername,
          accountType,
        },
      },
    });

    if (error) {
      console.error("Signup error:", error.message);
      setAuthError(formatAuthError(error));
      return;
    }

    try {
      localStorage.setItem("signupUsername", cleanUsername);
      localStorage.setItem("signupEmail", cleanEmail);

      if (!data?.user) {
        setAuthError("Signup completed, but the app could not read the new user.");
        return;
      }

      if (!data.session) {
        setAction("Login");
        sessionStorage.setItem("authMode", "Login");
        setPassword("");
        setAuthError(DEFAULT_SIGNUP_MESSAGE);
        return;
      }

      await ensureUserRecords(data.user, {
        username: cleanUsername,
        accountType,
      });
    } catch (e) {
      setAuthError(
        "Signup worked, but saving your profile failed. Check the Supabase users and businesses policies."
      );
      return;
    }

    onLogin(data.session);
    await routeAuthenticatedUser(data.user, {
      username: cleanUsername,
      accountType,
    });
  }

  async function logInAndRoute() {
    setAuthError("");

    if (isUsingFallbackSupabaseConfig) {
      console.warn("Login is using fallback Supabase config:", supabaseUrl);
    }

    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setAuthError("Enter your email address.");
      return;
    }

    if (!password.trim()) {
      setAuthError("Enter your password.");
      return;
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password,
    });

    if (error) {
      console.error("Login error:", error.message);
      setAuthError(formatAuthError(error));
      return;
    }

    if (!data?.session || !data?.user) {
      setAuthError("Login succeeded but could not read user id.");
      return;
    }

    try {
      onLogin(data.session);
      await routeAuthenticatedUser(data.user);
    } catch (profileErr) {
      console.error("Failed to ensure profile row:", profileErr.message);
      setAuthError(
        "Logged in, but could not load your profile. Check the Supabase users and businesses policies."
      );
    }
  }

  const handleSignUpClick = async () => {
    if (action !== "Sign Up") {
      setAction("Sign Up");
      setAuthError("");
      return;
    }
    await signUp();
  };

  const handleLoginClick = async () => {
    if (action !== "Login") {
      setAction("Login");
      setAuthError("");
      return;
    }
    await logInAndRoute();
  };

  return (
    <div className="container">
      <button className="back-button" onClick={() => onViewSelect("home")}>Back</button>

      <div className="header">
        <div className="text">{action}</div>
        <div className="underline"></div>
      </div>

      <div className="inputs">
        {action !== "Login" && (
          <div className="input">
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
        )}

        <div className="input">
          <input
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="input">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <button
            type="button"
            className="toggle-button"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
        </div>
      </div>

      {action !== "Login" && (
        <div className="account-type">
          <label>
            <input
              type="radio"
              name="accountType"
              value="User"
              checked={accountType === "User"}
              onChange={(e) => setAccountType(e.target.value)}
            />
            User Account
          </label>

          <label>
            <input
              type="radio"
              name="accountType"
              value="Business"
              checked={accountType === "Business"}
              onChange={(e) => setAccountType(e.target.value)}
            />
            Business Account
          </label>
        </div>
      )}

      {authError && (
        <div style={{ marginTop: 12, color: "crimson", fontSize: 14 }}>
          {authError}
        </div>
      )}

      <div className="submit-container">
        <button
          type="button"
          className={action === "Login" ? "submit gray" : "submit"}
          onClick={handleSignUpClick}
        >
          Sign Up
        </button>

        <button
          type="button"
          className={action === "Sign Up" ? "submit gray" : "submit"}
          onClick={handleLoginClick}
        >
          Login
        </button>
      </div>
    </div>
  );
};

export default LoginSignup;
